// src/App.tsx

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './components/LoginPage';
import TwoFactorAuth from './components/TwoFactorAuth';
import Sidebar from './components/Layout/Sidebar';
import ProtectedRoute from './components/Layout/ProtectedRoute';

import UserProfile from './components/UserProfile/UserProfileReal';
import MonthlyCertificates from './components/MonthlyCertificates/MonthlyCertificates';

import EmpresaApprovalPage from './components/Backoffice/EmpresaApproval/EmpresaApprovalPage';
import EmpresaManagementPage from './components/Backoffice/EmpresaManagement/EmpresaManagementPage';
import LicenseDashboardPage from './components/Backoffice/LicenseDashboard/LicenseDashboardPage';
import LicenseValidationPage from './components/Backoffice/LicenseValidation/LicenseValidationPage';
import WorkflowPage from './components/Backoffice/WorkflowValidation/WorkflowPage';

import DashboardKPI from './components/Operacional/DashboardKPI';
import MonitoramentoKanban from './components/Operacional/MonitoramentoKanban';
import EmissaoMTR from './components/Operacional/EmissaoMTR';
import RelatoriosDeclaracoes from './components/Operacional/RelatoriosDeclaracoes';
import DetalhesMTR from './components/Operacional/DetalhesMTR';
import ImportacaoMTR from './components/Operacional/ImportacaoMTR';
import CompraCreditos from './components/Checkout/CompraCreditos';
import FluxoPiloto from './components/Piloto/FluxoPiloto';
import AlertasDocumentosPage from './components/Alertas/AlertasDocumentosPage';
import UsuariosEmpresaPage from './components/Backoffice/UsuariosEmpresa/UsuariosEmpresaPage';
import LeitosPage from './components/Operacional/Leitos/LeitosPage';

const AppContent: React.FC = () => {
  const { isAuthenticated, twoFactorPending, user, logout } = useAuth();

  if (!isAuthenticated && !twoFactorPending) {
    return <LoginPage />;
  }

  if (twoFactorPending && user) {
    return <TwoFactorAuth />;
  }

  return (
          <BrowserRouter>
            <div className="min-h-screen bg-gray-100 flex">
              <Sidebar />

              <div className="flex-1 flex flex-col min-w-0">
                <header className="bg-white shadow border-b border-gray-200">
                  <div className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-emerald-700">
                        Gestão de Resíduos RSS
                      </h1>

                      <p className="text-sm text-gray-500">
                        Ambiente administrativo e operacional
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={logout}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                    >
                      Sair
                    </button>
                  </div>
                </header>

                <main className="flex-1 p-6 overflow-auto">
                  <Routes>
                    <Route path="/" element={<Navigate to="/piloto/fluxo" replace />} />

                    <Route path="/perfil" element={<UserProfile onLogout={logout} />} />
                    <Route
                      path="/piloto/fluxo"
                      element={
                        <ProtectedRoute permission="empresas.visualizar">
                          <FluxoPiloto />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/certificados"
                      element={
                        <ProtectedRoute permission="leitos.visualizar_certificados">
                          <MonthlyCertificates />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="/backoffice/aprovacao-empresas" element={<ProtectedRoute permission="empresas.aprovar"><EmpresaApprovalPage /></ProtectedRoute>} />
                    <Route path="/backoffice/gestao-empresas" element={<ProtectedRoute permission="empresas.visualizar"><EmpresaManagementPage /></ProtectedRoute>} />
                    <Route path="/backoffice/acessos-empresas" element={<ProtectedRoute permission="usuarios.gerir"><UsuariosEmpresaPage /></ProtectedRoute>} />
                    <Route path="/backoffice/dashboard-licencas" element={<ProtectedRoute permission="documentos.visualizar"><LicenseDashboardPage /></ProtectedRoute>} />
                    <Route path="/backoffice/alertas-documentos" element={<ProtectedRoute permission="documentos.visualizar"><AlertasDocumentosPage /></ProtectedRoute>} />
                    <Route path="/backoffice/validacao-licencas" element={<ProtectedRoute permission="documentos.validar"><LicenseValidationPage /></ProtectedRoute>} />
                    <Route path="/backoffice/workflow" element={<ProtectedRoute permission="validacoes.concluir"><WorkflowPage /></ProtectedRoute>} />

                    <Route path="/operacional/dashboard" element={<ProtectedRoute permission="auditoria.consultar"><DashboardKPI /></ProtectedRoute>} />
                    <Route path="/operacional/leitos" element={<ProtectedRoute permission="leitos.visualizar"><LeitosPage /></ProtectedRoute>} />
                    <Route path="/operacional/mtrs" element={<ProtectedRoute permission="mtr.criar"><MonitoramentoKanban /></ProtectedRoute>} />
                    <Route path="/operacional/mtrs/importacao" element={<ProtectedRoute permission="mtr.criar"><ImportacaoMTR /></ProtectedRoute>} />
                    <Route path="/operacional/mtrs/novo" element={<ProtectedRoute permission="mtr.criar"><EmissaoMTR /></ProtectedRoute>} />
                    <Route path="/operacional/mtrs/:id" element={<ProtectedRoute permission="mtr.criar"><DetalhesMTR /></ProtectedRoute>} />
                    <Route path="/operacional/relatorios" element={<ProtectedRoute permission="auditoria.consultar"><RelatoriosDeclaracoes /></ProtectedRoute>} />
                    <Route path="/Checkout/creditos" element={<ProtectedRoute permission="creditos.ajustar"><CompraCreditos /></ProtectedRoute>} />
                    <Route path="*" element={<Navigate to="/perfil" replace />} />
                  </Routes>
                </main>
              </div>
            </div>
          </BrowserRouter>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
